import {Component, computed, inject, Injector, OnInit, signal} from "@angular/core";
import {FetchDataType, FontAwesomeCode, ParticipantType, ScenarioRecording, SimUser, TrainingRoomType, TrainingSession} from "@models";
import {TranslateService} from "@ngx-translate/core";
import {NotificationService, SimRecorderService, SimUtilityService} from "@services";
import {Translations} from "@translations";
import {BasicMaterialDialog, MatDialogDataBaseInput} from "../../basic-material-dialog/basic-material-dialog.directive";
import {EventEmitterService} from "@event-emitter";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";

export interface DialogDataParticipantListDialogInput extends MatDialogDataBaseInput {
	participantType: ParticipantType;
	trainingSession: TrainingSession;
	trainingRoomType?: TrainingRoomType;
	trainingRoomId: string;
	isEditable: boolean;
	scenarioRecording: ScenarioRecording;
}

@Component({
	selector: "sim-participant-list-dialog",
	templateUrl: "./participant-list-dialog.component.html",
	styleUrls: ["./participant-list-dialog.component.scss"],
	providers: [EventEmitterService],
	animations: [zoomInOnEnterAnimation({duration: 175}), zoomOutOnLeaveAnimation({duration: 100})],
	standalone: false,
})
export class ParticipantListDialogComponent
	extends BasicMaterialDialog<ParticipantListDialogComponent, DialogDataParticipantListDialogInput>
	implements OnInit
{
	T = Translations;
	protected readonly ParticipantType = ParticipantType;
	protected readonly FetchDataType = FetchDataType;
	protected readonly isEditable = computed(() => this.data.isEditable);
	public users = signal<SimUser[]>([]);
	public headerIcon = signal<FontAwesomeCode>({} as FontAwesomeCode);
	public headerTextToken = signal<string>("");
	public userIds = computed<string[]>(() => this.users().map((x) => x.id) ?? []);

	readonly translate = inject(TranslateService);
	readonly #recorderService = inject(SimRecorderService);
	readonly #notificationService = inject(NotificationService);

	ngOnInit(): void {
		super.ngOnInit();
		this.initialize();
	}

	constructor(_injector: Injector) {
		super(_injector);
	}

	public removeUser(userToRemove: SimUser): void {
		if (!this.isEditable()) {
			return;
		}

		this.#recorderService
			.removeParticipant({
				scenarioRecordingId: this.data.scenarioRecording.id,
				trainingRoomId: this.data?.trainingRoomId || SimUtilityService.emptyGuid(),
				userId: userToRemove.id,
				participantType: this.data.participantType,
			})
			.then(() => this.listUsers())
			.catch(() =>
				this.#notificationService.notifyError(this.translate.instant(this.T.TRAINING_SESSION_PARTICIPANT_LIST_COULD_NOT_REMOVE)),
			);
	}

	public addUser(userToAdd: SimUser): void {
		if (!userToAdd || !this.isEditable()) {
			return;
		}
		this.#recorderService
			.addParticipant({
				scenarioRecordingId: this.data.scenarioRecording.id,
				trainingRoomId: this.data?.trainingRoomId || SimUtilityService.emptyGuid(),
				userId: userToAdd.id,
				participantType: this.data.participantType,
			})
			.then(() => this.listUsers())
			.catch(() =>
				this.#notificationService.notifyError(this.translate.instant(this.T.TRAINING_SESSION_PARTICIPANT_LIST_COULD_NOT_ADD)),
			);
	}

	protected initialize(): void {
		this.listUsers();
		switch (this.data.participantType) {
			case ParticipantType.Learner:
				this.headerIcon.set({
					fontSet: "fad",
					fontIcon: "fa-user-graduate",
				});
				this.headerTextToken.set(this.T.RECORDER_TXT_LEARNERS);
				break;
			case ParticipantType.Instructor:
				this.headerIcon.set({fontSet: "fad", fontIcon: "fa-user-md"});
				this.headerTextToken.set(this.T.RECORDER_TXT_FACILITATORS);
				break;
			case ParticipantType.StandardizedPatient:
				this.headerIcon.set({fontSet: "fad", fontIcon: "fa-user-injured"});
				this.headerTextToken.set(this.T.RECORDER_TXT_STANDARDIZEDPATIENTS);
				break;
		}
	}

	private listUsers(): void {
		const scenarioRecordingId = this.data.scenarioRecording.id;
		this.#recorderService.getParticipants(scenarioRecordingId, this.data.participantType).then(this.users.set);
	}
}
